import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashCompact from 'lodash/fp/compact';
import lodashOmit from 'lodash/fp/omit';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  TENANT_SCREENING_CRITERIA_MOVE_IN_MINIMUM_DAYS_DEFAULT,
  TENANT_SCREENING_CRITERIA_MOVE_IN_DATE_RANGE_IN_DAYS_DEFAULT,
} from 'common/tenantScreeningCriteriaConstants';

import {
  SR_API_ACTION_TENANT_SCREENING_CRITERIA_UPSERT,
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
} from 'api/actionTypes';

import {
  hideFieldIf,
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrEditListingBreedRestrictionsCta from 'client/components/SrEditListingBreedRestrictionsCta';
import SrFormikField, {
  SR_FIELD_THEME_PARTNER_FORM,
} from 'client/components/SrFormikField';

import postWithState from 'client/actions/postWithState';

function getFormFields({ formValues = {}, partnersList = [], isNew = true } = {}) {
  const formFields = lodashCompact([
    {
      id: 'id',
      type: 'hidden',
    },
    {
      id: 'listingId',
      type: 'hidden',
    },
    hideFieldIf(formValues.listingId || partnersList.length === 0, {
      id: 'partnerId',
      title: 'Partner',
      validations: ['required'],
      type: 'select',
      properties: {
        disabled: !isNew,
        choices: partnersList.map(({ id, displayName }) => ({
          label: displayName,
          value: id,
        })),
      },
    }),
    {
      id: 'creditScore',
      title: 'credit score',
      type: 'short_text',
      validations: ['required', 'creditScore'],
      properties: {
        placeholder: '700',
        pattern: '[0-9]*',
        onlyAcceptNumbers: true,
      },
    },
    {
      id: 'rentToIncomeRatio',
      title: 'rent to income ratio',
      label: '- 3 is the standard',
      type: 'number',
      validations: ['required'],
      properties: {
        placeholder: '3',
      },
    },
    {
      id: 'moveInMinimumDays',
      type: 'hidden',
    },
    {
      id: 'moveInDateRangeInDays',
      type: 'hidden',
    },
    {
      id: 'bankruptciesAccepted',
      title: 'accept bankruptcies?',
      type: 'yes_no',
      validations: ['required'],
    },
    formValues.bankruptciesAccepted && {
      id: 'bankruptciesDescription',
      title: 'bankruptcies description (timeframe where bankruptcies not allowed)',
      type: 'short_text',
      validations: ['required'],
      properties: {
        placeholder: 'in the past 18 months',
      },
    },
    formValues.bankruptciesAccepted && {
      id: 'bankruptciesExpirationInMonths',
      title: 'bankruptcies expiration in months',
      type: 'short_text',
      validations: ['required'],
      properties: {
        placeholder: '18',
        pattern: '[0-9]*',
        onlyAcceptNumbers: true,
      },
    },
    {
      id: 'evictionsAccepted',
      title: 'accept evictions ?',
      type: 'yes_no',
      validations: ['required'],
    },
    formValues.evictionsAccepted && {
      id: 'evictionsDescription',
      title: 'evictions description (timeframe where evictions not allowed)',
      type: 'short_text',
      validations: ['required'],
      properties: {
        placeholder: 'in the past 3 years',
      },
    },
    formValues.evictionsAccepted && {
      id: 'evictionsExpirationInMonths',
      title: 'evictions expiration in months',
      type: 'short_text',
      validations: ['required'],
      properties: {
        placeholder: '36',
        pattern: '[0-9]*',
        onlyAcceptNumbers: true,
      },
    },
    {
      id: 'section8Accepted',
      title: 'enrolled in and accept section 8?',
      type: 'yes_no',
      validations: ['required'],
    },
    {
      id: 'guarantorAccepted',
      title: 'accept guarantor?',
      type: 'yes_no',
      validations: ['required'],
    },
    {
      id: 'extraApplicantRequirements',
      title: 'extra applicant requirements',
      type: 'long_text',
      tooltipCopy: (
        'Anything important the applicant needs to know that is not already addressed above. ' +
        '(Example: You must have seen the property in-person before signing the lease.)'
      ),
    },
  ]);

  return formFields;
}

class SrTenantScreeningCriteriaModal extends Component {
  static propTypes = {
    defaultValues: PropTypes.object,
    partnersList: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    omitApiActions: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    onCopyPartnerRestrictedBreeds: PropTypes.func,
  }

  static defaultProps = {
    defaultValues: {},
  }

  constructor(props) {
    super(props);
    this.state = {
      breedRestrictions: null,
    };
  }

  _onBreedRestrictionsSubmit = (breedRestrictions) => {
    this.setState({
      breedRestrictions,
    });
  }

  _onSubmitForm = (formValues) => {
    const {
      dispatch,
      onClose,
      onSubmit,
      omitApiActions,
    } = this.props;

    const {
      breedRestrictions,
    } = this.state;

    const tenantScreeningCriteria = setNullEmptyValues(
      lodashOmit(['partnerId', 'listingId'], formValues)
    );

    if (!tenantScreeningCriteria.bankruptciesAccepted) {
      tenantScreeningCriteria.bankruptciesDescription = null;
      tenantScreeningCriteria.bankruptciesExpirationInMonths = null;
    }
    if (!tenantScreeningCriteria.evictionsAccepted) {
      tenantScreeningCriteria.evictionsDescription = null;
      tenantScreeningCriteria.evictionsExpirationInMonths = null;
    }

    if (omitApiActions) {
      onSubmit({
        ...tenantScreeningCriteria,
        breedRestrictions,
      });
    }
    else if (tenantScreeningCriteria.id) {
      dispatch(postWithState, {
        action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
        payload: {
          table: 'tenantScreeningCriteria',
          where: {
            id: tenantScreeningCriteria.id,
          },
          updates: tenantScreeningCriteria,
        },
        onSuccess: () => {
          onSubmit({
            ...tenantScreeningCriteria,
            breedRestrictions,
          });
          onClose();
        },
      });
    }
    else {
      dispatch(postWithState, {
        action: SR_API_ACTION_TENANT_SCREENING_CRITERIA_UPSERT,
        payload: {
          partnerId: parseInt(formValues.partnerId, 10) || null,
          listingId: parseInt(formValues.listingId, 10) || null,
          tenantScreeningCriteria: lodashOmit(['id'], setNullEmptyValues(tenantScreeningCriteria)),
        },
        onSuccess: (tenantScreeningCriteria) => {
          onSubmit(tenantScreeningCriteria);
          onClose();
        },
      });
    }
  }

  render() {
    const {
      defaultValues,
      partnersList,
      onClose,
      omitApiActions,
      onCopyPartnerRestrictedBreeds,
    } = this.props;

    const isNew = !defaultValues.id;
    const listingId = defaultValues.listingId;

    return (
      <SrDarkModal className='SrTenantScreeningCriteriaModal'>
        <SrModalContent onCloseIconClick={onClose}>
          <Formik
            validate={(values) => {
              return createFormValidationFn(getFormFields({ formValues: values }))(values);
            }}
            initialValues={createInitialValues({
              formFields: getFormFields({
                formValues: defaultValues,
                partnersList,
                isNew,
              }),
              defaultValues: {
                moveInMinimumDays: TENANT_SCREENING_CRITERIA_MOVE_IN_MINIMUM_DAYS_DEFAULT,
                moveInDateRangeInDays: TENANT_SCREENING_CRITERIA_MOVE_IN_DATE_RANGE_IN_DAYS_DEFAULT,
                ...defaultValues,
              },
            })}
            onSubmit={this._onSubmitForm}
            render={(formikBag) => {
              const formFields = getFormFields({
                formValues: formikBag.values,
                partnersList,
                isNew,
              });

              return (
                <Form className='form' autoComplete='off'>
                  <div className='container'>
                    <SrText size='2xl' weight='bold' color='blue'>Tenant Screening Criteria</SrText>
                    <div>
                      <SrText
                        size='16'
                        weight='600'
                        color='grey400'
                      >
                        {listingId
                          ? 'Changing the criteria below will only affect this property.'
                          : 'You can always change this later'
                        }
                      </SrText>
                    </div>
                    <SrBox paddingTop='10px'>
                      <SrEditListingBreedRestrictionsCta
                        listingId={listingId}
                        onSubmit={() => {
                          this._onBreedRestrictionsSubmit();
                          formikBag.submitForm();
                        }}
                        preserveEditBreedRestrictionValues={omitApiActions}
                      >
                        <SrText
                          color='blue300'
                          cursor='pointer'
                          size='16'
                          weight='600'
                        >
                          Edit Restricted Breeds
                        </SrText>
                      </SrEditListingBreedRestrictionsCta>
                      {onCopyPartnerRestrictedBreeds && (
                        <SrText
                          color='blue300'
                          cursor='pointer'
                          size='16'
                          weight='600'
                          onClick={onCopyPartnerRestrictedBreeds}
                        >
                          Copy restricted breeds from Partner settings
                        </SrText>
                      )}
                    </SrBox>
                    <div className='content'>
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          className={SR_FIELD_THEME_PARTNER_FORM}
                        />
                      ))}
                    </div>
                    <div>
                      <div style={{ height: 20 }} />
                      <ButtonDeprecated
                        type='button'
                        onClick={formikBag.handleSubmit}
                        disabled={!formikBag.isValid}
                      >
                        {isNew ? 'Create' : 'Update'}
                      </ButtonDeprecated>
                    </div>
                  </div>
                </Form>
              );
            }}
          />
        </SrModalContent>
      </SrDarkModal>
    );
  }
}

SrTenantScreeningCriteriaModal.displayName = 'SrTenantScreeningCriteriaModal';

export default SrTenantScreeningCriteriaModal;
